import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SquareWarnIcon,
  CurrencyDollarIcon,
  CheckLinearIcon,
  RequiredIcon,
  UpgradeWarnIcon
} from '@/components/common/icon';
import {
  getPlatformVideoContentType,
  getPlatformVideoContentTypeDesc
} from '@/biz/platform';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { OrderStatus } from '@/interface/order';
import Radio from '@/components/Radio/index';
import { AdsCampaignMarketingStrategy } from '@/interface/campaign';
import { ProductType, ProductPhysicalSelectEnum } from '../config';
import axiosInstance from '../../../apis/axiosInstance';
import { isFromApp, isMobile } from '@/biz/tool';
import { Input, Switch, Button, toast } from 'aha-components';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { usePlatformStore } from '@/hooks/usePlatformStore';
import { BasePriceKeyListMap } from '@/interface/common';
import { formatPriceDollar } from '@/biz/order';
const PriceFieldsComponent = ({
  direction = 'flex flex-col',
  hasRadioButton = false,
  orderPriceAdjustment = false,
  newOrderPrice,
  setNewOrderPrice,
  maxOrderPrice,
  nonNegotiable = false,
  setNonNegotiable = () => {}
}: {
  direction?: 'flex flex-row' | 'flex flex-col';
  hasRadioButton?: boolean;
  orderPriceAdjustment?: boolean;
  newOrderPrice?: number | string;
  setNewOrderPrice?: (v: number | string) => void;
  maxOrderPrice?: number | null;
  nonNegotiable?: boolean;
  setNonNegotiable?: (v: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { gbPricingSchemaFlag } = useMultiplePricesStore();
  const { setShowModalShowImage } = useWebModalStore();
  const { user, setUser } = usePlatformStore();
  const { order, campaign } = detailData;
  const priceFields = order?.priceFields;
  const interestedPriceFields = order?.interestedPriceFields;
  const [setBaseRateLoading, setSetBaseRateLoading] = useState(false);
  const [setBaseRateSuccess, setSetBaseRateSuccess] = useState(false);

  // 用来判断输入的价格是否超过系统计算的最大价格
  const [creatorMaxActualGet, setCreatorMaxActualGet] = useState<number | null>(
    null
  );
  const [
    creatorMaxActualGetByCampaignCpm,
    setCreatorMaxActualGetByCampaignCpm
  ] = useState<number | null>(null);
  const [
    creatorMaxActualGetByCampaignPrice,
    setCreatorMaxActualGetByCampaignPrice
  ] = useState<number | null>(null);
  const [
    isPriceHigherThanCampaignMultiplier,
    setIsPriceHigherThanCampaignMultiplier
  ] = useState(false);
  const { selectedPriceFields, setSelectedPriceFields } =
    useMultiplePricesStore();

  // 先请求下改价范围
  const getSetPriceRange = async (selectedData?: any) => {
    const interestedPriceFields = selectedData || selectedPriceFields;
    if (interestedPriceFields?.length === 0) {
      return;
    }
    const params = {
      interestedPriceField: {
        ...interestedPriceFields[0]
      }
    };
    const { data } = await axiosInstance.post(
      `/order/${detailData?.order?.id}/competitivePrice`,
      params
    );
    setCreatorMaxActualGet(
      data?.data?.interestedPriceField?.creatorMaxActualGet
    );
    // 根据campaign cpm 计算的最大价格
    if (data?.data?.interestedPriceField?.creatorMaxActualGetByCampaignCpm) {
      setCreatorMaxActualGetByCampaignCpm(
        data?.data?.interestedPriceField?.creatorMaxActualGetByCampaignCpm
      );
    }
    // 根据campaign price 计算的最大价格
    if (data?.data?.interestedPriceField?.creatorMaxActualGetByCampaignPrice) {
      setCreatorMaxActualGetByCampaignPrice(
        data?.data?.interestedPriceField?.creatorMaxActualGetByCampaignPrice
      );
    }
  };

  // 切换价格套餐后，重新对比价格提示
  useEffect(() => {
    if (creatorMaxActualGet != null && newOrderPrice != null) {
      if (+newOrderPrice >= creatorMaxActualGet) {
        setIsPriceHigherThanCampaignMultiplier(true);
        // setNewOrderPrice?.(creatorMaxActualGet);
      } else {
        setIsPriceHigherThanCampaignMultiplier(false);
      }
    }
    // 价格限制不同，和更新价格后，重置base rate 设置成功状态
    setSetBaseRateSuccess(false);
  }, [creatorMaxActualGet, newOrderPrice]);

  // 切换选中的订单类型后，重置base rate 设置成功状态
  useEffect(() => {
    setSetBaseRateSuccess(false);
  }, [selectedPriceFields]);

  useEffect(() => {
    // 为了达人改价后没有刷新页面的情况下，通弹窗中按钮又回到这个页面，那么selectedPriceFields的套餐价格是上次操作完后改价的内容
    if (
      selectedPriceFields.length > 0 &&
      priceFields &&
      priceFields.length > 0
    ) {
      priceFields.forEach((e: any) => {
        if (e.contentType === selectedPriceFields[0].contentType) {
          setSelectedPriceFields([e]);
        }
      });
    }
    getSetPriceRange();
  }, []);

  const setAsBaseRate = async () => {
    setSetBaseRateLoading(true);
    const currentPlatform = order?.platform;
    const currentContentType =
      selectedPriceFields?.[0]?.contentType ||
      interestedPriceFields?.[0]?.contentType ||
      getPlatformVideoContentType({
        platform: order?.platform
      });
    const currentInputPrice = Number(newOrderPrice || 0);

    const previousBaselineData =
      user?.baselineConfig && typeof user.baselineConfig === 'object'
        ? { ...user.baselineConfig }
        : {};
    const baselineKey =
      BasePriceKeyListMap[currentPlatform]?.[currentContentType];
    const newBaselineData = {
      ...previousBaselineData,
      [baselineKey as string]: currentInputPrice
    };
    try {
      const { data } = await axiosInstance.post(
        '/user/baselineConfig',
        newBaselineData
      );
      if (data.code === 0) {
        toast({
          message: t('price.fields.set_your_rate.set_base_price_success', {
            defaultValue: 'Set as base rate successfully!'
          }),
          position: 'top-right',
          duration: 2000,
          showClose: false,
          type: 'success'
        });
        const { data: infoData } = await axiosInstance.get(
          `/influencer/info?from=${isFromApp() ? 'app' : 'web'}`
        );
        const latestUser = infoData?.data?.user;
        const latestBaselineConfig = infoData?.data?.user?.baselineConfig;
        if (latestUser && typeof latestUser === 'object') {
          setUser(latestUser);
        } else if (
          latestBaselineConfig &&
          typeof latestBaselineConfig === 'object'
        ) {
          setUser({
            ...user,
            baselineConfig: latestBaselineConfig
          });
        }
        if (!setBaseRateSuccess) {
          setSetBaseRateSuccess(true);
        }
      }
    } catch (error) {
      toast({
        message: t('price.fields.set_your_rate.set_base_price_error', {
          defaultValue: 'Set as base rate failed!'
        }),
        position: 'top-right',
        duration: 2000,
        showClose: false,
        type: 'error'
      });
      console.error('setAsBaseRate failed:', error);
    } finally {
      setSetBaseRateLoading(false);
    }
  };

  // 设置输入的价格为平台底价
  const setNewOrderPriceToPlatformBasePrice = () => {
    return (
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 border-t border-solid border-[#E7E5E4] mt-4 pt-4 pl-8 pb-1">
        <div className="test-[#57534D] text-sm font-normal leading-5">
          {t('price.fields.set_your_rate.set_base_price_title', {
            defaultValue:
              'Stick with this rate? Set it as your base rate so future deals match your price range.'
          })}
        </div>
        <div>
          <Button
            type="default"
            size="small"
            loading={setBaseRateLoading}
            onClick={() => {
              setAsBaseRate();
            }}
          >
            <div
              className={`flex items-center gap-2 whitespace-nowrap ${setBaseRateSuccess && !setBaseRateLoading ? 'text-[#079455]' : ''}`}
            >
              {setBaseRateSuccess && !setBaseRateLoading ? (
                <CheckLinearIcon className="w-5 h-5" />
              ) : null}
              {setBaseRateSuccess && !setBaseRateLoading
                ? t('price.fields.set_your_rate.set_base_price_btn_2', {
                    defaultValue: 'Base rate set to {{price}}',
                    price: formatPriceDollar(newOrderPrice)
                  })
                : t('price.fields.set_your_rate.set_base_price_btn', {
                    defaultValue: 'Set as base rate'
                  })}
            </div>
          </Button>
        </div>
      </div>
    );
  };

  // 根据最大价格计算出价格超过类型
  const getPriceExceedsType = () => {
    // 三种情况、CPM、campaign价格、系统限制的订单价格，前两种合并成一个提示
    if (
      (creatorMaxActualGetByCampaignCpm &&
        creatorMaxActualGet === creatorMaxActualGetByCampaignCpm) ||
      (creatorMaxActualGetByCampaignPrice &&
        creatorMaxActualGet === creatorMaxActualGetByCampaignPrice)
    ) {
      return 'campaign_price_cpm';
    }
    return 'max_order_price';
  };

  if (!gbPricingSchemaFlag) {
    // 暂未拿到 ab 测试结果
    return (
      <div className="w-full flex justify-center items-center gap-8 ">
        {t('price.fields.loading', { defaultValue: 'loading price...' })}
      </div>
    );
  }
  // no.0 Commission rate
  if (
    order?.marketingStrategy ===
    AdsCampaignMarketingStrategy.AFFILIATES_MARKETING
  ) {
    if (!hasRadioButton) {
      return (
        <div className="w-full flex flex-col justify-center items-center gap-8 ">
          <div className="w-full self-stretch px-4 py-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-[#e8e8ec] inline-flex flex-col justify-center items-start gap-2">
            <div className="self-stretch bg-white inline-flex justify-between items-center">
              <div className="w-[186px] justify-start text-[#1c2024] text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-snug">
                {t('price.fields.commission.title', {
                  defaultValue: 'Commission'
                })}
              </div>
              <div className="justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
                {order?.commissionRate * 100 || '--'}%
              </div>
            </div>
            <div className="self-stretch justify-start text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
              {t('price.fields.commission.desc', {
                defaultValue:
                  'You gain {{rate}}% of the total sales generated from your affiliate link',
                rate: order?.commissionRate * 100 || '--'
              })}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="self-stretch p-4 rounded-lg outline outline-[1.50px] outline-offset-[-1.50px] outline-[#5b3de9] inline-flex flex-col justify-start items-start gap-2">
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-[#8b8d98] text-xs font-semibold font-['Plus_Jakarta_Sans'] leading-[18px]">
                {t('price.fields.total_payment', {
                  defaultValue: 'Total payment'
                })}
              </div>
              <div className="w-4 h-4 relative rounded-full outline outline-2 outline-offset-[-2px] outline-[#5a31f0] overflow-hidden">
                <div className="w-2 h-2 left-[4px] top-[4px] absolute bg-[#5a31f0] rounded-full" />
              </div>
            </div>
            <div className="justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
              {t('price.fields.commission.rate', {
                defaultValue: '{{rate}}% commission generated from the sales',
                rate: order?.commissionRate * 100 || '--'
              })}
            </div>
          </div>
          <div className="self-stretch justify-start text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
            {t('price.fields.commission.desc', {
              defaultValue:
                'You gain {{rate}}% of the total sales generated from your affiliate link',
              rate: order?.commissionRate * 100 || '--'
            })}
          </div>
        </div>
      );
    }
  }

  // no.1 老订单
  // old 单一内容类型定价
  if (!priceFields?.length) {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-8 ">
        <PriceFieldItem
          priceData={{
            price: order?.price,
            contentType: getPlatformVideoContentType({
              platform: order?.platform
            })
          }}
          alwaysShowPrice
          hasRadioButton={hasRadioButton}
        />
        {order?.status.includes(OrderStatus.Invited) && (
          <p>
            {t('price.fields.abnormal_order', {
              defaultValue: 'The order is abnormal, please contact'
            })}{' '}
            <a
              className="text-[#0c67e6] cursor-pointer"
              href="mailto:support@creator.ahacreator.com"
            >
              support@creator.ahacreator.com
            </a>
            .
          </p>
        )}
      </div>
    );
  }

  // no.2 新订单，已提交价格
  if (interestedPriceFields?.length > 0) {
    return (
      <div className="w-full justify-center items-center gap-4 ">
        {interestedPriceFields?.map((el: any, index: number) => {
          return (
            <React.Fragment key={`${el.contentType}-${index}`}>
              <PriceFieldItem
                priceData={el}
                alwaysShowPrice
                hasRadioButton={hasRadioButton}
              />
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // no.3 新订单，未提交价格，gbPricingSchemaFlag是 not_display_price_can_change
  if (priceFields?.length && !interestedPriceFields?.length) {
    // 加入前 不展示定价
    if (gbPricingSchemaFlag === 'not_display_price_can_change') {
      return (
        <div className="w-full flex justify-center items-center gap-8 ">
          <span>
            {t('price.fields.open_budget', { defaultValue: 'Open budget' })}
          </span>
        </div>
      );
    }
  }

  // 一处样式代码，用于展示基础价格设置成功后的提示
  const renderBenefitsUnlocked = () => {
    const currentContentType =
      selectedPriceFields?.[0]?.contentType ||
      interestedPriceFields?.[0]?.contentType;
    const baseKey = BasePriceKeyListMap[order?.platform]?.[currentContentType];
    const savedBaseRate = baseKey
      ? Number(user?.baselineConfig?.[baseKey]) || 0
      : 0;
    const inputPrice = Number(newOrderPrice) || 0;

    if (!savedBaseRate || inputPrice !== savedBaseRate) return null;

    return (
      <div
        className={`bg-gradient-to-r from-[#FFEAD5] to-[#fff] rounded-[8px] p-3 flex items-center justify-between`}
      >
        <div className="flex flex-col justify-center items-start gap-2">
          <div
            className={`text-[#79716B] text-xs font-semibold uppercase leading-4 tracking-[4.80px]`}
          >
            {t('onboarding.base_rate.unlock_benefits', {
              defaultValue: 'Benefits unlocked'
            })}
          </div>
          <div className="flex flex-col justify-start items-start gap-2">
            <div className="inline-flex justify-start items-start gap-2">
              <RequiredIcon className="text-[#FB6011]" />
              <div className="justify-start text-[#44403C] text-sm font-medium leading-5">
                {t('price.fields.set_your_rate.unlock_benefits_1', {
                  defaultValue:
                    'Spend less time negotiating, more time creating'
                })}
              </div>
            </div>
            <div className="inline-flex justify-start items-start gap-2">
              <RequiredIcon className="text-[#FB6011]" />
              <div className="justify-start text-[#44403C] text-sm font-medium leading-5">
                {t('price.fields.set_your_rate.unlock_benefits_2', {
                  defaultValue:
                    'Priority visibility in recommendations to brands'
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // no.4 新订单，未提交价格
  return (
    <>
      {hasRadioButton &&
        detailData?.product?.productType === ProductType.PHYSICAL_PRODUCT &&
        detailData?.product?.physicalProductShipType ===
          ProductPhysicalSelectEnum.ship && (
          <div className="px-4 py-2 bg-[#EFF6FF] rounded-lg -mb-3 flex items-start gap-2">
            <SquareWarnIcon className="w-6 h-6" />
            <div
              className={`flex ${isMobile() ? 'flex-col justify-start items-start' : 'items-center'}`}
            >
              <span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('price.fields.receiving_product', {
                      defaultValue:
                        'You’ll receive a free product (worth <b>${{price}}</b>) as part of this collaboration. Many creators consider this when setting their price',
                      price: detailData?.product?.physicalProductCashValue
                    })
                  }}
                ></span>
                <span>{'.'}</span>
              </span>
            </div>
          </div>
        )}
      {/*  */}
      <div
        className={`w-full justify-center items-stretch gap-4
        ${direction}
        `}
      >
        {priceFields?.map((el: any, index: number) => {
          return (
            <React.Fragment key={`${el.contentType}-${index}`}>
              <PriceFieldItem
                priceData={el}
                alwaysShowPrice
                hasRadioButton={hasRadioButton}
                getSetPriceRange={getSetPriceRange}
              />
            </React.Fragment>
          );
        })}
      </div>
      {orderPriceAdjustment && (
        <>
          <div className="flex flex-col gap-4">
            <div className="text-[#0D0D0D] text-base font-semibold leading-6">
              {t('price.fields.set_your_rate.title', {
                defaultValue: 'Set your rate'
              })}
            </div>
            <div className="max-w-[320px]">
              <Input
                type="Number"
                disabled={detailData?.order?.fixedCreatorEarnAmount}
                value={newOrderPrice as string}
                onChange={(e) => {
                  if (+e.target.value > 0) {
                    setNewOrderPrice?.(e.target.value);
                  } else {
                    setNewOrderPrice?.('');
                  }
                }}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                prefix={<CurrencyDollarIcon className="w-4 h-4" />}
                suffix={'USD'}
              />
            </div>
            {renderBenefitsUnlocked()}
            {(creatorMaxActualGet && isPriceHigherThanCampaignMultiplier) ||
            (newOrderPrice &&
              maxOrderPrice &&
              +newOrderPrice >= +maxOrderPrice) ? (
              <div className="bg-[#FEF2F2] leading-5 px-4 py-3 rounded-lg">
                <div className="flex items-start gap-2">
                  <div className="mt-[2px]">
                    <UpgradeWarnIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-[#292524] text-base font-semibold">
                      {t(
                        `price.fields.set_your_rate.${getPriceExceedsType()}_title`,
                        {
                          max_price: maxOrderPrice || creatorMaxActualGet
                        }
                      )}
                    </div>
                    <div className="text-[#292524] text-sm font-normal mt-2">
                      {t(
                        `price.fields.set_your_rate.${getPriceExceedsType()}_desc`,
                        {
                          max_price: maxOrderPrice || creatorMaxActualGet
                        }
                      )}
                    </div>
                  </div>
                </div>
                {setNewOrderPriceToPlatformBasePrice()}
              </div>
            ) : null}
            <div className="w-full h-24 bg-gradient-to-b from-gray-100 to-gray-100/0 rounded-2xl px-[10px] py-2">
              <div className="border-2 border-[#E5E7EB] rounded-lg h-full w-full flex justify-between items-center p-[14px]">
                <div className="flex flex-col gap-2">
                  <div className="text-[#292524] text-sm font-semibold leading-5">
                    {t('price.fields.set_your_rate.fixed_rate', {
                      defaultValue: 'Make this rate fixed (non-negotiable)'
                    })}
                  </div>
                  <div className="text-[#57534D] text-xs font-normal leading-4">
                    {t('price.fields.set_your_rate.fixed_rate_desc', {
                      defaultValue:
                        'Once turned on, the brand can only accept or decline.'
                    })}
                  </div>
                </div>
                <div>
                  <Switch
                    className={`${!nonNegotiable ? '!bg-[#D1D1DB]' : ''}`}
                    checked={nonNegotiable}
                    onChange={(checked) => setNonNegotiable?.(checked)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="bg-[#FAFAFA] p-4 rounded-lg flex flex-col gap-4">
            <div className="mb-1 justify-start text-text-color-primary text-base font-semibold font-['Plus_Jakarta_Sans'] leading-snug">
              {t('price.fields.set_your_rate.set_your_rate_desc', {
                defaultValue: 'Set a rate that works for you'
              })}
            </div>
            <div
              className={`flex gap-4 ${isMobile() ? 'justify-start items-start' : 'items-center'}`}
            >
              <div className="w-[68px]">
                <img
                  className="w-[68px] h-[68px]"
                  src={'/visibility-img.png'}
                  alt="Visibility"
                />
              </div>
              <div className="flex-1 text-sm gap-1 flex flex-col">
                <div className="text-[#1C1917] font-semibold leading-5">
                  {t('price.fields.set_your_rate.visibility.title', {
                    defaultValue: 'Visibility'
                  })}
                </div>
                <div className="text-[#44403C]">
                  {t('price.fields.set_your_rate.visibility.desc', {
                    defaultValue:
                      'Competitive pricing helps you get discovered sooner and turn more opportunities into collaborations.'
                  })}
                </div>
              </div>
              {!isMobile() && (
                <div
                  className="cursor-pointer"
                  onClick={() =>
                    setShowModalShowImage(true, '/visibility-img-2.png')
                  }
                >
                  <img
                    className="w-[120px] h-[68px]"
                    src={'/visibility-img-2.png'}
                    alt="Visibility"
                  />
                </div>
              )}
            </div>
            {isMobile() && (
              <div>
                <img
                  className="w-full"
                  src={'/visibility-img-2.png'}
                  alt="Visibility"
                />
              </div>
            )}
            <div
              className={`flex gap-4 ${isMobile() ? 'justify-start items-start' : 'items-center'}`}
            >
              <div className="w-[68px]">
                <img
                  className="w-[68px] h-[68px]"
                  src={'/long-term-growth-img.png'}
                  alt="Long-term growth"
                />
              </div>
              <div className="flex-1 text-sm gap-1 flex flex-col">
                <div className="text-[#1C1917] font-semibold leading-5">
                  {t('price.fields.set_your_rate.long_term_growth.title', {
                    defaultValue: 'Long-term growth'
                  })}
                </div>
                <div className="text-[#44403C] w-[calc(100%-20px)]">
                  {t('price.fields.set_your_rate.long_term_growth.desc', {
                    defaultValue:
                      'Reasonable rates help you reach agreement more smoothly and build long-term brand partnerships on AhaCreator.'
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const PriceFieldItem = ({
  priceData,
  alwaysShowPrice,
  hasRadioButton = false,
  getSetPriceRange
}: {
  priceData: any;
  alwaysShowPrice?: boolean;
  hasRadioButton?: boolean;
  getSetPriceRange?: (selectedData?: any) => void;
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { order, product } = detailData;
  const interestedPriceFields = order?.interestedPriceFields;

  const { selectedPriceFields, setSelectedPriceFields, gbPricingSchemaFlag } =
    useMultiplePricesStore();
  const { contentType, price, originPrice, actualGet } = priceData;
  let showPrice = actualGet;
  // if (order?.status.includes(OrderStatus.Invited)) {
  //   // Invited 的时候 可以更改 price字段。
  //   // 且 只有 Invited 的时候 优先展示 originPrice 字段
  //   showPrice = originPrice;
  // }

  return (
    <div
      className={`gap-1 flex flex-col justify-start items-start border-2 border-solid rounded-lg px-4 py-3 w-full
      ${
        selectedPriceFields?.find((el) => el?.contentType === contentType) ||
        interestedPriceFields?.find(
          (el: any) => el?.contentType === contentType
        )
          ? 'border-theme-color'
          : 'border-[#e8e8ec]'
      }
          ${
            interestedPriceFields?.length > 0 ||
            !order?.status.includes(OrderStatus.Invited)
              ? 'cursor-default'
              : 'cursor-pointer'
          }
      `}
      onClick={() => {
        if (
          interestedPriceFields?.length > 0 ||
          !order?.status.includes(OrderStatus.Invited)
        ) {
          return;
        }
        setSelectedPriceFields([priceData]);
        getSetPriceRange?.([priceData]);
      }}
    >
      <div className="flex justify-between w-full">
        <div
          className={`flex justify-between w-full flex-col items-start gap-2
        `}
        >
          <div
            className={`text-[#1C1917] text-[12px] leading-snug font-semibold`}
          >
            {getPlatformVideoContentType({
              platform: order?.platform,
              contentType: contentType
            })}
          </div>
        </div>
        {hasRadioButton && (
          <Radio
            checked={Boolean(
              selectedPriceFields?.find((el) => el?.contentType === contentType)
            )}
          />
        )}
      </div>
      <div className="text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
        {getPlatformVideoContentTypeDesc(
          getPlatformVideoContentType({
            platform: order?.platform,
            contentType: contentType
          }),
          order?.platform
        )}
      </div>
    </div>
  );
};

export default PriceFieldsComponent;
